import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Building2,
  Monitor,
  FileBarChart,
  Shield,
  ShieldCheck,
  Server,
  HardDrive,
  Sparkles,
  Activity,
  Settings,
  Users,
  RefreshCw,
  ChevronDown,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'

const mainNav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/customers', label: 'Customers', icon: Building2, hideUnlessMultiCustomer: true },
  { to: '/agents', label: 'Devices', icon: Monitor },
  {
    id: 'backup',
    label: 'Backup',
    icon: HardDrive,
    children: [
      { to: '/nas', label: 'NAS Backup', icon: Server },
      { to: '/backup', label: 'Server Backup', icon: HardDrive },
    ],
  },
  {
    id: 'endpoint-security',
    label: 'Endpoint security',
    icon: Shield,
    children: [
      { to: '/threatdown', label: 'ThreatDown', icon: Shield },
      { to: '/defender', label: 'Windows Defender', icon: ShieldCheck },
    ],
  },
  { to: '/reports', label: 'Reports', icon: FileBarChart },
  { to: '/assistant', label: 'Assistant', icon: Sparkles },
]

const adminNav = [
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/sync', label: 'Sync', icon: RefreshCw },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
]

const adminOnlyNav = adminNav.filter((item) => item.to !== '/admin/sync')

function NavItem({ to, label, icon: Icon, end, indented = false, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 border-l-[3px] border-transparent py-2 text-sm font-medium transition-colors',
          indented ? 'px-3 pl-9' : 'px-3',
          isActive
            ? cn(
                'border-white bg-white/12 font-semibold text-white',
                indented ? 'pl-[calc(2.25rem-3px)]' : 'pl-[calc(0.75rem-3px)]',
              )
            : 'text-sidebar-foreground/70 hover:bg-white/8 hover:text-white',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {label}
    </NavLink>
  )
}

function NavGroup({ id, label, icon: Icon, children, onNavigate }) {
  const { pathname } = useLocation()
  const childActive = children.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  )
  const [open, setOpen] = useState(childActive)

  useEffect(() => {
    if (childActive) setOpen(true)
  }, [childActive])

  return (
    <div>
      <button
        type="button"
        id={`nav-group-${id}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex w-full items-center gap-3 border-l-[3px] border-transparent px-3 py-2 text-sm font-medium transition-colors',
          childActive
            ? 'text-white'
            : 'text-sidebar-foreground/70 hover:bg-white/8 hover:text-white',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open ? 'rotate-0' : '-rotate-90')}
        />
      </button>
      {open && (
        <div className="space-y-0.5" role="group" aria-labelledby={`nav-group-${id}`}>
          {children.map((item) => (
            <NavItem key={item.to} {...item} indented onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  )
}

function SidebarBody({ showAdmin, showStaff, isCustomer, canFilterCustomers, onNavigate, showClose, onClose }) {
  const navItems = mainNav.filter((item) => {
    if (isCustomer && item.hideForCustomer) return false
    if (item.hideUnlessMultiCustomer && isCustomer && !canFilterCustomers) return false
    return true
  })

  return (
    <>
      <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-bold">
          BD
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-none">Bekat Dash</p>
          <p className="text-xs text-sidebar-foreground/50">Reporting</p>
        </div>
        {showClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-sidebar-foreground/70 hover:bg-white/8 hover:text-white"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-4">
        <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
          Overview
        </p>
        {navItems.map((item) =>
          item.children ? (
            <NavGroup key={item.id} {...item} onNavigate={onNavigate} />
          ) : (
            <NavItem key={item.to} {...item} onNavigate={onNavigate} />
          ),
        )}

        {showAdmin && (
          <>
            <Separator className="my-4 bg-sidebar-border" />
            <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
              Administration
            </p>
            <NavItem to="/admin/sync" label="Sync" icon={RefreshCw} onNavigate={onNavigate} />
            <NavItem
              to="/admin/ingest"
              label="RMM ingest"
              icon={Activity}
              onNavigate={onNavigate}
            />
            {adminOnlyNav.map((item) => (
              <NavItem key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-sidebar-border px-4 py-3 text-xs text-sidebar-foreground/50">
        Bekat Dash v{import.meta.env.VITE_APP_VERSION || 'dev'}
      </div>
    </>
  )
}

export function Sidebar({
  showAdmin = false,
  showStaff = false,
  isCustomer = false,
  canFilterCustomers = false,
  mobileOpen = false,
  onClose,
  desktopHidden = false,
}) {
  const bodyProps = { showAdmin, showStaff, isCustomer, canFilterCustomers }

  return (
    <>
      <aside
        className={cn(
          'h-full w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
          desktopHidden ? 'hidden' : 'hidden lg:flex',
        )}
      >
        <SidebarBody {...bodyProps} />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close navigation"
            onClick={onClose}
          />
          <aside className="relative flex h-full w-64 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl">
            <SidebarBody
              {...bodyProps}
              showClose
              onClose={onClose}
              onNavigate={onClose}
            />
          </aside>
        </div>
      ) : null}
    </>
  )
}
